type LocaleType = {
  header: {
    title: string
  }
  fields: {
    firstName: { label: string; placeholder: string }
    lastName: { label: string; placeholder: string }
    phone: { label: string; placeholder: string }
    email: { label: string; placeholder: string }
    purchasePrice: { label: string; placeholder: string }
    contactMethod: {
      label: string
      options: { label: string; value: string }[]
    }
    hasProperty: {
      label: string
      options: { label: string; value: string }[]
    }
  }
  errors: {
    firstName: { required: string; min: string }
    lastName: { required: string; min: string }
    phone: { required: string; invalid: string }
    email: { required: string; invalid: string }
    purchasePrice: { required: string; min: string }
    contactMethod: { required: string }
    hasProperty: { required: string }
  }
  actionButton: {
    title: string
  }
  regulatoryInfo: {
    title: string
    paragraphs: string[]
  }
}

const LOCALE_KEYS = ['pt-BR', 'en-US', 'es-ES'] as const
type Locale = typeof LOCALE_KEYS[number]
type ByLocale<T> = Record<Locale, T>

const NMLS_DISCLAIMER =
  'Inter US Finance LLC | NMLS 1161874 | 501 Brickell Bay Dr Ste 202, FL 33131'

const HEADER: ByLocale<LocaleType['header']> = {
  'pt-BR': { title: 'Detalhes do financiamento' },
  'en-US': { title: 'Your mortgage details' },
  'es-ES': { title: 'Acerca de tu hipoteca' },
}

const FIRST_NAME_FIELD: ByLocale<LocaleType['fields']['firstName']> = {
  'pt-BR': { label: 'Nome', placeholder: 'Digite seu nome' },
  'en-US': { label: 'Name', placeholder: 'Type your first name' },
  'es-ES': { label: 'Nombre', placeholder: 'Escriba su nombre' },
}

const LAST_NAME_FIELD: ByLocale<LocaleType['fields']['lastName']> = {
  'pt-BR': { label: 'Sobrenome', placeholder: 'Digite seu sobrenome completo' },
  'en-US': { label: 'Last name', placeholder: 'Type your last name' },
  'es-ES': { label: 'Apellido', placeholder: 'Escriba su apellido completo' },
}

const PHONE_FIELD: ByLocale<LocaleType['fields']['phone']> = {
  'pt-BR': { label: 'Telefone', placeholder: '(00) 00000-0000' },
  'en-US': { label: 'Phone', placeholder: '+1 Area code + phone number' },
  'es-ES': { label: 'Teléfono', placeholder: '+54 Código de área + teléfono' },
}

const EMAIL_FIELD: ByLocale<LocaleType['fields']['email']> = {
  'pt-BR': { label: 'E-mail', placeholder: 'nome@email.com.br' },
  'en-US': { label: 'E-mail', placeholder: 'name@email.com' },
  'es-ES': { label: 'E-mail', placeholder: 'nombre@email.com' },
}

const PURCHASE_PRICE_FIELD: ByLocale<LocaleType['fields']['purchasePrice']> = {
  'pt-BR': { label: 'Valor do imóvel', placeholder: '0.00' },
  'en-US': { label: 'Home purchase price', placeholder: '0.00' },
  'es-ES': { label: 'Valor de la propriedad', placeholder: '0.00' },
}

const CONTACT_METHOD_FIELD: ByLocale<LocaleType['fields']['contactMethod']> = {
  'pt-BR': {
    label: 'Como prefere ser contatado?',
    options: [
      { label: 'E-mail', value: 'Email' },
      { label: 'Telefone', value: 'Phone' },
      { label: 'WhatsApp', value: 'WhatsApp' },
    ],
  },
  'en-US': {
    label: 'Preferred contact method',
    options: [
      { label: 'E-mail', value: 'Email' },
      { label: 'Phone', value: 'Phone' },
      { label: 'WhatsApp', value: 'WhatsApp' },
    ],
  },
  'es-ES': {
    label: 'Contacto de preferencia',
    options: [
      { label: 'E-mail', value: 'Email' },
      { label: 'Teléfono', value: 'Phone' },
      { label: 'WhatsApp', value: 'WhatsApp' },
    ],
  },
}

const HAS_PROPERTY_FIELD: ByLocale<LocaleType['fields']['hasProperty']> = {
  'pt-BR': {
    label: 'Você já escolheu o imóvel?',
    options: [
      { label: 'Sim, já escolhi', value: 'true' },
      { label: 'Não escolhi', value: 'false' },
    ],
  },
  'en-US': {
    label: 'Have you selected the property?',
    options: [
      { label: 'Yes, I selected', value: 'true' },
      { label: 'I did not select', value: 'false' },
    ],
  },
  'es-ES': {
    label: '¿Ya seleccionaste la propiedad?',
    options: [
      { label: 'Sí, seleccioné', value: 'true' },
      { label: 'No seleccioné', value: 'false' },
    ],
  },
}

const FIRST_NAME_ERROR: ByLocale<LocaleType['errors']['firstName']> = {
  'pt-BR': { required: 'Nome é obrigatório', min: 'Informe ao menos 2 caracteres' },
  'en-US': { required: 'Name is required', min: 'Enter at least 2 characters' },
  'es-ES': { required: 'El nombre es obligatorio', min: 'Ingresa al menos 2 caracteres' },
}

const LAST_NAME_ERROR: ByLocale<LocaleType['errors']['lastName']> = {
  'pt-BR': { required: 'Sobrenome é obrigatório', min: 'Informe ao menos 2 caracteres' },
  'en-US': { required: 'Last name is required', min: 'Enter at least 2 characters' },
  'es-ES': { required: 'El apellido es obligatorio', min: 'Ingresa al menos 2 caracteres' },
}

const PHONE_ERROR: ByLocale<LocaleType['errors']['phone']> = {
  'pt-BR': { required: 'Telefone é obrigatório', invalid: 'Formato incorreto' },
  'en-US': { required: 'Phone is required', invalid: 'Incorrect format' },
  'es-ES': { required: 'El teléfono es obligatorio', invalid: 'Formato inválido' },
}

const EMAIL_ERROR: ByLocale<LocaleType['errors']['email']> = {
  'pt-BR': { required: 'E-mail é obrigatório', invalid: 'E-mail incorreto' },
  'en-US': { required: 'Email is required', invalid: 'Incorrect email' },
  'es-ES': { required: 'El correo es obligatorio', invalid: 'Correo inválido' },
}

const PURCHASE_PRICE_ERROR: ByLocale<LocaleType['errors']['purchasePrice']> = {
  'pt-BR': { required: 'Valor do imóvel é obrigatório', min: 'Valor mínimo de $ 100 mil' },
  'en-US': {
    required: 'Home purchase price is required',
    min: 'The minimum amount is $100,000',
  },
  'es-ES': {
    required: 'El precio de compra es obligatorio',
    min: 'El valor mínimo debe ser 100 mil',
  },
}

const CONTACT_METHOD_ERROR: ByLocale<LocaleType['errors']['contactMethod']> = {
  'pt-BR': { required: 'Selecione uma forma de contato' },
  'en-US': { required: 'Select a contact method' },
  'es-ES': { required: 'Selecciona un método de contacto' },
}

const HAS_PROPERTY_ERROR: ByLocale<LocaleType['errors']['hasProperty']> = {
  'pt-BR': { required: 'Selecione se você já escolheu o imóvel' },
  'en-US': { required: 'Select whether you have chosen the property' },
  'es-ES': { required: 'Selecciona si ya elegiste la propiedad' },
}

const ACTION_BUTTON: ByLocale<LocaleType['actionButton']> = {
  'pt-BR': { title: 'Enviar informações' },
  'en-US': { title: 'Send information' },
  'es-ES': { title: 'Enviar información' },
}

const REGULATORY_INFO: ByLocale<LocaleType['regulatoryInfo']> = {
  'pt-BR': {
    title: 'Avisos legais e regulatórios',
    paragraphs: [
      'Inter US Finance LLC é um credor com igualdade de oportunidades. Taxas, termos e programas sujeitos a alterações sem aviso prévio. O refinanciamento do empréstimo existente do mutuário pode aumentar os encargos financeiros totais ao longo da vida do empréstimo.',
      'As informações não levam em consideração a situação financeira pessoal do mutuário, pontuação de crédito verificada, renda, dívidas existentes ou outros fatores. Isto não é um compromisso de empréstimo. As cotações não vinculativas não são Estimativas de Empréstimo oficiais sob o Reg. Z.',
      NMLS_DISCLAIMER,
    ],
  },
  'en-US': {
    title: 'Regulatory information',
    paragraphs: [
      'Inter US Finance LLC is an equal opportunity lender. Rate, terms and programs subject to change without notice. Refinancing the borrowers existing loan, may increase total finance charges over the life of the loan.',
      "The information does not take into consideration borrower's personal financial situation, verified credit score, income, existing debt, or other factors. This is not a commitment to lend. The non-binding quotes are not official Loan Estimates under Reg. Z.",
      NMLS_DISCLAIMER,
    ],
  },
  'es-ES': {
    title: 'Información regulatoria',
    paragraphs: [
      'Inter US Finance LLC es un prestamista con igualdad de oportunidades. Las tasas, términos y programas están sujetos a cambios sin previo aviso. El refinanciamiento del préstamo existente del prestatario puede aumentar los cargos financieros totales durante la vida del préstamo.',
      'La información no tiene en cuenta la situación financiera personal del prestatario, el puntaje de crédito verificado, los ingresos, las deudas existentes u otros factores. Esto no es un compromiso de préstamo. Las cotizaciones no vinculantes no son Estimaciones de Préstamo oficiales bajo el Reg. Z.',
      NMLS_DISCLAIMER,
    ],
  },
}

const buildLocale = (locale: Locale): LocaleType => ({
  header: HEADER[locale],
  fields: {
    firstName: FIRST_NAME_FIELD[locale],
    lastName: LAST_NAME_FIELD[locale],
    phone: PHONE_FIELD[locale],
    email: EMAIL_FIELD[locale],
    purchasePrice: PURCHASE_PRICE_FIELD[locale],
    contactMethod: CONTACT_METHOD_FIELD[locale],
    hasProperty: HAS_PROPERTY_FIELD[locale],
  },
  errors: {
    firstName: FIRST_NAME_ERROR[locale],
    lastName: LAST_NAME_ERROR[locale],
    phone: PHONE_ERROR[locale],
    email: EMAIL_ERROR[locale],
    purchasePrice: PURCHASE_PRICE_ERROR[locale],
    contactMethod: CONTACT_METHOD_ERROR[locale],
    hasProperty: HAS_PROPERTY_ERROR[locale],
  },
  actionButton: ACTION_BUTTON[locale],
  regulatoryInfo: REGULATORY_INFO[locale],
})

export const locales: Record<string, LocaleType> = Object.fromEntries(
  LOCALE_KEYS.map((locale): [Locale, LocaleType] => [locale, buildLocale(locale)]),
)
